import { hasStreetNumber } from "@/lib/loqate/hasStreetNumber"
import { type AddressVerificationResult } from "@/types/loqate/verify"

/**
 * Markets where every deliverable address carries a street number, so a line without one is
 * a mistake rather than a rural address that genuinely has none.
 */
const HOUSE_NUMBER_REQUIRED_COUNTRIES: ReadonlySet<string> = new Set([
    "AT",
    "BE",
    "DE",
    "DK",
    "FI",
    "NL",
    "NO",
    "SE"
])

export interface FallbackAddressInput {
    address1: string
    address2?: string
    country: string
}

/**
 * The one thing still worth saying once Loqate is unreachable. Advisory only — turning
 * customers away during someone else's outage costs more than an undeliverable parcel.
 */
export const fallbackVerificationResult = (
    address: FallbackAddressInput
): AddressVerificationResult | null => {
    if (!HOUSE_NUMBER_REQUIRED_COUNTRIES.has(address.country.toUpperCase())) {
        return null
    }

    // The number reaches Loqate on either line, so either line satisfies the check.
    // Apartment tokens (Lgh 1901) are not a street number.
    if (hasStreetNumber(`${address.address1} ${address.address2 ?? ""}`)) {
        return null
    }

    return {
        decision: "fix",
        issues: [
            {
                code: "house_number_missing",
                field: "address1",
                value: address.address1.trim()
            }
        ],
        avc: null,
        verificationUnavailable: true
    }
}
